import React, { ReactNode, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/system';
import InputLabel from '@mui/material/InputLabel';

import { inputBorderLight, dividerBorderLight } from 'src/common/styles';
import { ItemType } from 'src/components/common/field/TypeCustom';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 16;

type DropDownProps = {
  selected: number | string | null;
  placeholder?: string;
  idSelect: string;
  items?: ItemType[];
  defaultSelect?: string;
  sx?: SxProps<Theme>;
  sxLabel?: SxProps<Theme>;
  sxInput?: SxProps<Theme>;
  sxMenuProps?: SxProps<Theme>;
  onChange: (id: number | string) => void;
  error?: boolean;
  svgIconUrl?: string;
};

const DropDown: React.FC<DropDownProps> = (props) => {
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [openDrop, setOpenDrop] = useState(false);

  useEffect(() => {
    props.selected !== 0 && props.selected !== null
      ? setShowPlaceholder(false)
      : setShowPlaceholder(true);
    // eslint-disable-next-line
  }, [props.selected]);

  const handleChange = (event: SelectChangeEvent<string>, child: ReactNode) => {
    const {
      target: { value },
    } = event;

    props.onChange(value);
  };

  const handleOnClickOpen = () => {
    setOpenDrop(!openDrop);
  };

  return (
    <Box onClick={handleOnClickOpen}>
      <FormControl fullWidth sx={props.sx}>
        {showPlaceholder &&
          props.placeholder &&
          props.placeholder.length !== 0 && (
            <InputLabel
              id='select-placeholder-label'
              htmlFor={props.idSelect}
              sx={{
                color: '#AAB2BD',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '14.4px',
                '&.Mui-focused': {
                  color: '#AAB2BD',
                  transform: 'translate(14px, 16px) scale(1)',
                },
                '&.MuiInputLabel-root': {
                  color: '#AAB2BD',
                  transform: 'translate(14px, 16px) scale(1)',
                },
                cursor: 'pointer',
                ...props.sxLabel,
              }}
            >
              {props.placeholder}
            </InputLabel>
          )}
        {props.items && (
          <Select
            labelId='select-placeholder-label'
            id={props.idSelect}
            value={
              props.selected === null || props.selected === 0
                ? ''
                : String(props.selected)
            }
            sx={{
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                '& .MuiTypography-root': {
                  whiteSpace: 'nowrap ', // Prevents text from wrapping
                  overflow: 'hidden', // Hides overflow text
                  textOverflow: 'ellipsis', // Displays ellipsis (...) for overflow text
                },
              },
              ...inputBorderLight,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: props.error ? '#d32f2f !important' : '',
              },
              ...props.sxInput,
            }}
            open={openDrop}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: ITEM_HEIGHT * 3 + ITEM_PADDING_TOP,
                  backgroundColor: 'primary.light',
                  ...dividerBorderLight,
                  borderRadius: '4px',
                  ...props.sxMenuProps,
                },
              },
            }}
            onChange={handleChange}
            IconComponent={
              props.svgIconUrl
                ? () => (
                    <img
                      src={props.svgIconUrl}
                      alt='dropdown icon'
                      style={{
                        width: 20,
                        height: 20,
                        marginRight: '10px',
                        cursor: 'pointer',
                      }}
                    />
                  )
                : ArrowDropDownIcon
            }
          >
            {props.defaultSelect && (
              <MenuItem
                value=''
                sx={{
                  height: '40px',
                  borderBottom: '1px solid',
                  ...dividerBorderLight,
                }}
              >
                <Typography
                  ml='4px'
                  fontWeight='400'
                  fontSize='14px'
                  lineHeight='14px'
                  sx={{ opacity: 0.5 }}
                >
                  {props.defaultSelect}
                </Typography>
              </MenuItem>
            )}
            {props.items &&
              props.items.map((value, index) => (
                <MenuItem
                  key={index}
                  value={String(value.id)}
                  sx={{
                    height: '40px',
                    borderBottom:
                      props.items && index === props.items.length - 1
                        ? 'none'
                        : '1px solid',
                    ...dividerBorderLight,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  <Typography
                    ml='4px'
                    fontWeight='400'
                    fontSize='14px'
                    lineHeight='14px'
                    sx={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {value.name}
                  </Typography>
                </MenuItem>
              ))}
          </Select>
        )}
      </FormControl>
    </Box>
  );
};

export default DropDown;
